import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Building2, ShieldAlert, Eye, KeyRound, Activity, Users, ArrowRight } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMeta } from "@/lib/useMeta";

const when = (v) => (v ? new Date(v).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="border border-border bg-card p-4" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <div className="text-2xl font-semibold mt-1.5 tabular-nums">{value}</div>
  </div>
);

const Panel = ({ title, children, testid }) => (
  <section className="border border-border bg-card" data-testid={testid}>
    <h2 className="px-4 py-2.5 border-b border-border text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{title}</h2>
    <div className="max-h-[280px] overflow-auto">{children}</div>
  </section>
);

const Empty = ({ text }) => <p className="px-4 py-6 text-[12.5px] text-muted-foreground">{text}</p>;

export default function SuperAdmin() {
  useMeta({ title: "Super Admin Console | Plan-Sarthi", description: "Platform security and tenancy console.", path: "/admin" });
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [reassign, setReassign] = useState({ email: "", company_name: "", reason: "" });
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .get("/admin/security-overview")
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(apiError(err.response?.data?.detail)));

  useEffect(() => {
    load();
  }, []);

  const submitReassign = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/reassign-company", reassign);
      toast.success("Company reassigned and logged in the audit trail");
      setReassign({ email: "", company_name: "", reason: "" });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="superadmin-page">
      <header className="border-b border-border bg-[#0A1628] text-white">
        <div className="max-w-7xl mx-auto px-5 py-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-baseline">
              <span className="text-lg font-bold tracking-tight">Plan</span>
              <span className="text-lg font-bold" style={{ color: "#C9A84C" }}>-</span>
              <span className="text-lg font-bold tracking-tight">Sarthi</span>
              <span className="ml-3 text-[10px] uppercase tracking-[0.2em]" style={{ color: "#C9A84C" }}>Super Admin Console</span>
            </div>
            <p className="text-[12px] text-white/50 mt-1">{user?.email}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/leads"><Button size="sm" variant="outline" className="bg-transparent text-white border-white/25" data-testid="link-leads">Leads Desk</Button></Link>
            <Link to="/admin/data-editor"><Button size="sm" variant="outline" className="bg-transparent text-white border-white/25" data-testid="link-data-editor">Data Editor</Button></Link>
            <Link to="/enquiries"><Button size="sm" variant="outline" className="bg-transparent text-white border-white/25" data-testid="link-enquiries">Enquiries</Button></Link>
            <Link to="/projects"><Button size="sm" style={{ background: "#C9A84C", color: "#0A1628" }} data-testid="link-projects">Projects <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button></Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 py-7 space-y-6">
        {!data ? (
          <p className="text-sm text-muted-foreground">Loading console…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Stat icon={Building2} label="Companies" value={data.companies.length} testid="stat-companies" />
              <Stat icon={Activity} label="Active sessions" value={data.active_sessions} testid="stat-sessions" />
              <Stat icon={ShieldAlert} label="Blocked attempts" value={data.denied.length} testid="stat-denied" />
              <Stat icon={Eye} label="Cross-company views" value={data.cross_company.length} testid="stat-cross" />
            </div>

            <Panel title="Companies — isolated workspaces" testid="panel-companies">
              <table className="w-full text-[12.5px]">
                <thead className="text-left text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="px-4 py-2 font-medium">Company</th>
                    <th className="px-4 py-2 font-medium">Users</th>
                    <th className="px-4 py-2 font-medium">Projects</th>
                    <th className="px-4 py-2 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {data.companies.map((c) => (
                    <tr key={c.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-2">{c.name}</td>
                      <td className="px-4 py-2 tabular-nums">{c.users}</td>
                      <td className="px-4 py-2 tabular-nums">{c.projects}</td>
                      <td className="px-4 py-2 text-muted-foreground">{when(c.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>

            <div className="grid lg:grid-cols-2 gap-6">
              <Panel title="Blocked cross-company attempts" testid="panel-denied">
                {data.denied.length === 0 ? <Empty text="No blocked attempts recorded." /> : (
                  <ul className="divide-y divide-border text-[12.5px]">
                    {data.denied.map((d) => (
                      <li key={d.id} className="px-4 py-2.5">
                        <div className="font-medium">{d.email}</div>
                        <div className="text-muted-foreground text-[11.5px]">{d.endpoint} · {when(d.at)}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>

              <Panel title="Super Admin cross-company views (read-only audit)" testid="panel-cross">
                {data.cross_company.length === 0 ? <Empty text="No cross-company access recorded." /> : (
                  <ul className="divide-y divide-border text-[12.5px]">
                    {data.cross_company.map((d) => (
                      <li key={d.id} className="px-4 py-2.5">
                        <div className="font-medium">{d.admin_email}</div>
                        <div className="text-muted-foreground text-[11.5px]">
                          viewed {d.company_viewed} · {d.reason} · {when(d.at)}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>

              <Panel title="Recent logins — timestamp, IP, device" testid="panel-logins">
                {data.logins.length === 0 ? <Empty text="No logins recorded yet." /> : (
                  <ul className="divide-y divide-border text-[12.5px]">
                    {data.logins.map((l) => (
                      <li key={l.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                        <div>
                          <div className="font-medium">{l.email}</div>
                          <div className="text-muted-foreground text-[11.5px]">{l.ip} · {l.device} · {when(l.at)}</div>
                        </div>
                        <span className={`text-[10.5px] uppercase tracking-wider ${l.result === "success" ? "text-emerald-600" : "text-red-500"}`}>
                          {l.result}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>

              <Panel title="Company reassignments (audit trail)" testid="panel-reassignments">
                {data.reassignments.length === 0 ? <Empty text="No reassignments recorded." /> : (
                  <ul className="divide-y divide-border text-[12.5px]">
                    {data.reassignments.map((r) => (
                      <li key={r.id} className="px-4 py-2.5">
                        <div className="font-medium">{r.target_email}</div>
                        <div className="text-muted-foreground text-[11.5px]">
                          by {r.admin_email} · {r.reason} · {when(r.at)}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
            </div>

            <section className="border border-border bg-card p-5" data-testid="reassign-form-section">
              <h2 className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" /> Reassign a user to another company
              </h2>
              <p className="text-[12.5px] text-muted-foreground mt-1.5">
                Only the Super Admin can move a user between workspaces. Every reassignment is permanently logged with
                timestamp, reason and admin identity.
              </p>
              <form onSubmit={submitReassign} className="mt-4 grid sm:grid-cols-3 gap-3" data-testid="reassign-form">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider">User email</Label>
                  <Input type="email" required value={reassign.email} onChange={(e) => setReassign({ ...reassign, email: e.target.value })} data-testid="reassign-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider">New company</Label>
                  <Input required value={reassign.company_name} onChange={(e) => setReassign({ ...reassign, company_name: e.target.value })} data-testid="reassign-company-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider">Reason</Label>
                  <Input required value={reassign.reason} onChange={(e) => setReassign({ ...reassign, reason: e.target.value })} data-testid="reassign-reason-input" />
                </div>
                <Button type="submit" className="sm:col-span-3" disabled={busy} data-testid="reassign-submit-btn">
                  <KeyRound className="w-4 h-4 mr-1.5" /> {busy ? "Reassigning…" : "Reassign & log"}
                </Button>
              </form>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
